<script>
import { onBeforeUnmount, ref, watch } from 'vue';

export default {
  props: {
    isVisible: { type: Boolean, default: true },
    markers: { type: Array, default: () => [] },
    colors: { type: Object, default: () => ({}) },
  },
  emits: ['toggle', 'isolate'],
  setup(props, { emit }) {
    let hoverTimer = null;
    const isolatedMarker = ref(null);

    function computeDefaultToggledOff(markersList) {
      if (!markersList || !Array.isArray(markersList)) return [];
      // Default show LAD, RCA, LCx and hide Global unless Global is the only available territory
      if (markersList.includes('Global') && markersList.length > 1) {
        return ['Global'];
      }
      return [];
    }

    const toggledOffMarkers = ref(
      new Set(computeDefaultToggledOff(props.markers)),
    );

    watch(
      () => props.markers,
      (newMarkers) => {
        clearTimeout(hoverTimer);
        isolatedMarker.value = null;
        emit('isolate', null);

        const defaults = computeDefaultToggledOff(newMarkers);
        toggledOffMarkers.value = new Set(defaults);
        emit('toggle', defaults);
      },
      { deep: true, immediate: true },
    );

    function onItemClick(marker) {
      clearTimeout(hoverTimer);
      if (toggledOffMarkers.value.has(marker)) {
        toggledOffMarkers.value.delete(marker);
      }
      else {
        toggledOffMarkers.value.add(marker);
      }
      toggledOffMarkers.value = new Set(toggledOffMarkers.value);
      emit('toggle', Array.from(toggledOffMarkers.value));
    }

    function onMouseEnter(marker) {
      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => {
        isolatedMarker.value = marker;
        emit('isolate', marker);
      }, 800);
    }

    function onMouseLeave() {
      clearTimeout(hoverTimer);
      if (isolatedMarker.value) {
        isolatedMarker.value = null;
        emit('isolate', null);
      }
    }

    onBeforeUnmount(() => {
      clearTimeout(hoverTimer);
    });

    const isToggledOff = marker => toggledOffMarkers.value.has(marker);

    return {
      isolatedMarker,
      toggledOffMarkers,
      isToggledOff,
      onItemClick,
      onMouseEnter,
      onMouseLeave,
    };
  },
};
</script>

<template>
  <Transition name="slide-fade">
    <div
      v-if="isVisible"
      class="legend-container"
      @mouseleave="onMouseLeave"
    >
      <div
        v-for="(marker, index) in markers"
        :key="index"
        class="legend-item"
        :class="{
          'dimmed': isolatedMarker && isolatedMarker !== marker,
          'isolated': isolatedMarker === marker,
          'toggled-off': isToggledOff(marker) && isolatedMarker !== marker,
        }"
        :title="
          isToggledOff(marker)
            ? `${marker}: Hidden. Click to show, hold to isolate`
            : `${marker}: Visible. Click to hide, hold to isolate`
        "
        @click="onItemClick(marker)"
        @mouseenter="onMouseEnter(marker)"
        @mouseleave="onMouseLeave"
      >
        <div
          class="legend-stroke"
          :style="{ background: colors[marker] }"
        />
        <h6 class="legend-text">
          {{ marker }}
        </h6>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.legend-container {
  pointer-events: none;
  position: absolute;
  bottom: 15px;
  left: 45px;
  right: 38px;
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  box-sizing: border-box;
}

.legend-item {
  pointer-events: auto;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  box-sizing: border-box;
  padding: 3px 8px;
  border-radius: 6px;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease,
    background-color 0.2s ease;
  user-select: none;
  opacity: 1;
}

.legend-item:hover {
  transform: scale(1.05);
  background-color: rgba(255, 255, 255, 0.08);
}

.legend-item.dimmed {
  opacity: 0.18;
}

.legend-item.toggled-off {
  opacity: 0.35;
}

.legend-item.toggled-off .legend-stroke {
  opacity: 0.4;
  filter: grayscale(40%);
}

.legend-item.toggled-off .legend-text {
  color: rgba(255, 255, 255, 0.45);
  text-decoration: line-through;
  text-decoration-color: rgba(255, 255, 255, 0.3);
}

.legend-item.isolated {
  opacity: 1;
  transform: scale(1.08);
  background-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.15);
}

.legend-item.isolated .legend-text {
  font-weight: 600;
}

.legend-text {
  font-weight: 400;
  font-size: 11px;
  text-align: left;
  margin: 0;
  color: white;
  transition: color 0.2s ease;
}

.legend-stroke {
  width: 24px;
  height: 3px;
  border-radius: 20px;
  transition: opacity 0.2s ease;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(5px);
  opacity: 0;
}
</style>
